/// <reference types="./syncGetEvents.d.ts" />
/* eslint-disable @typescript-eslint/no-unused-expressions */
import { EventType, notOk, ok, } from '@dossierhq/core';
import { buildSqliteSqlQuery, } from '@dossierhq/database-adapter';
import { queryMany, queryNoneOrOne } from '../QueryFunctions.js';
import { assertExhaustive, assertIsDefined } from '../utils/AssertUtils.js';
export async function managementSyncGetEvents(database, context, query) {
    const nextIdResult = await resolveAfterId(database, context, query);
    if (nextIdResult.isError())
        return nextIdResult;
    const afterId = nextIdResult.value;
    const eventRowsResult = await getEvents(database, context, query, afterId);
    if (eventRowsResult.isError())
        return eventRowsResult;
    const { eventRows, hasMore } = eventRowsResult.value;
    const entitiesInfoResult = await getEntityInfoForEvents(database, context, eventRows);
    if (entitiesInfoResult.isError())
        return entitiesInfoResult;
    const convertResult = convertEventRowsToPayload(query.after, eventRows, entitiesInfoResult.value);
    if (convertResult.isError())
        return convertResult;
    return ok({ events: convertResult.value, hasMore });
}
async function resolveAfterId(database, context, query) {
    let afterId = null;
    if (query.after !== null) {
        const cursorResult = await queryNoneOrOne(database, context, buildSqliteSqlQuery(({ sql }) => {
            sql `SELECT id FROM events WHERE uuid = ${query.after}`;
        }));
        if (cursorResult.isError())
            return cursorResult;
        afterId = cursorResult.value?.id ?? null;
        if (afterId === null) {
            return notOk.BadRequest(`No such event (${query.after})`);
        }
    }
    return ok(afterId);
}
async function getEvents(database, context, query, afterId) {
    const result = await queryMany(database, context, buildSqliteSqlQuery(({ sql }) => {
        sql `SELECT e.id, e.uuid, e.type, e.created_at, s.uuid AS created_by, sv.version, sv.specification, p.provider, p.identifier FROM events e`;
        sql `JOIN subjects s ON e.created_by = s.id`;
        sql `LEFT JOIN schema_versions sv ON e.schema_versions_id = sv.id`; // only available on schema events
        sql `LEFT JOIN principals p ON e.principals_id = p.id`; // only available on principal events
        if (afterId !== null) {
            sql `WHERE e.id > ${afterId}`;
        }
        sql `ORDER BY e.id ASC LIMIT ${query.limit + 1}`;
    }));
    if (result.isError())
        return result;
    const eventRows = result.value;
    const hasMore = eventRows.length > query.limit;
    eventRows.splice(query.limit);
    return ok({ eventRows, hasMore });
}
async function getEntityInfoForEvents(database, context, eventRows) {
    const entityEventIds = eventRows
        .filter((it) => it.type !== EventType.updateSchema)
        .map((it) => it.id);
    if (entityEventIds.length === 0) {
        return ok([]);
    }
    // Get shared info that is needed by all entity events (the superset except what's needed only for create/update)
    const sharedResult = await queryMany(database, context, buildSqliteSqlQuery(({ sql, addValueList }) => {
        sql `SELECT eev.events_id, eev.published_name, e.uuid, e.uuid_before_delete, e.auth_key, e.resolved_auth_key, ev.version`;
        sql `FROM event_entity_versions eev`;
        sql `JOIN entity_versions ev ON eev.entity_versions_id = ev.id`;
        sql `JOIN entities e ON ev.entities_id = e.id`;
        sql `WHERE eev.events_id IN ${addValueList(entityEventIds)}`;
    }));
    if (sharedResult.isError())
        return sharedResult;
    const sharedRows = sharedResult.value;
    // Get the info that is only needed for create/update events (includes fields which can be heavy)
    const createOrUpdateEventIds = eventRows
        .filter(({ type }) => type === EventType.createEntity ||
        type === EventType.createAndPublishEntity ||
        type === EventType.updateEntity ||
        type === EventType.updateAndPublishEntity)
        .map((it) => it.id);
    let createOrUpdateRows = [];
    if (createOrUpdateEventIds.length > 0) {
        const createOrUpdateResult = await queryMany(database, context, buildSqliteSqlQuery(({ sql, addValueList }) => {
            sql `SELECT eev.events_id, e.uuid, ev.name, ev.type, ev.schema_version, ev.fields`;
            sql `FROM event_entity_versions eev`;
            sql `JOIN entity_versions ev ON eev.entity_versions_id = ev.id`;
            sql `JOIN entities e ON ev.entities_id = e.id`;
            sql `WHERE eev.events_id IN ${addValueList(createOrUpdateEventIds)}`;
        }));
        if (createOrUpdateResult.isError())
            return createOrUpdateResult;
        createOrUpdateRows = createOrUpdateResult.value;
    }
    // Merge all the entity info
    const payload = {};
    // For all events, we need the shared info for every entity
    for (const { events_id, ...rest } of sharedRows) {
        if (!payload[events_id]) {
            payload[events_id] = [];
        }
        payload[events_id].push(rest);
    }
    // For create/update events, we add the create/update info for every entity
    for (const { events_id, uuid, ...rest } of createOrUpdateRows) {
        const existingInfo = payload[events_id];
        const entityInfoIndex = existingInfo.findIndex((it) => it.uuid === uuid);
        existingInfo[entityInfoIndex] = { ...existingInfo[entityInfoIndex], ...rest };
    }
    return ok(payload);
}
function convertEventRowsToPayload(previousId, eventRows, entityInfo) {
    const events = [];
    for (const eventRow of eventRows) {
        const { type } = eventRow;
        const parentId = previousId;
        previousId = eventRow.uuid;
        if (type === EventType.createPrincipal) {
            events.push(makeEvent(type, parentId, eventRow, {
                provider: eventRow.provider,
                identifier: eventRow.identifier,
            }));
            continue;
        }
        if (type === EventType.updateSchema) {
            const schemaSpecification = {
                version: eventRow.version,
                ...JSON.parse(eventRow.specification),
            };
            events.push(makeEvent(type, parentId, eventRow, { schemaSpecification }));
            continue;
        }
        // The rest of the events need entity info
        const eventEntityInfo = entityInfo[eventRow.id];
        switch (type) {
            case EventType.archiveEntity: {
                const entityInfo = eventEntityInfo[0];
                events.push(makeEvent(type, parentId, eventRow, {
                    entity: { id: resolveId(entityInfo), version: entityInfo.version },
                }));
                break;
            }
            case EventType.createEntity:
            case EventType.createAndPublishEntity: {
                const entityInfo = eventEntityInfo[0];
                if (!('fields' in entityInfo)) {
                    return notOk.Generic('Cannot find extended info about entity');
                }
                events.push(makeEvent(type, parentId, eventRow, {
                    entity: {
                        id: resolveId(entityInfo),
                        info: {
                            type: entityInfo.type,
                            name: entityInfo.name,
                            authKey: entityInfo.auth_key,
                            resolvedAuthKey: entityInfo.resolved_auth_key,
                            schemaVersion: entityInfo.schema_version,
                        },
                        fields: JSON.parse(entityInfo.fields),
                    },
                }));
                break;
            }
            case EventType.deleteEntities: {
                events.push(makeEvent(type, parentId, eventRow, {
                    entities: eventEntityInfo.map((it) => ({ id: resolveId(it), version: it.version })),
                }));
                break;
            }
            case EventType.publishEntities: {
                events.push(makeEvent(type, parentId, eventRow, {
                    entities: eventEntityInfo.map((it) => ({
                        id: resolveId(it),
                        version: it.version,
                        publishedName: it.published_name,
                    })),
                }));
                break;
            }
            case EventType.unarchiveEntity: {
                const entityInfo = eventEntityInfo[0];
                events.push(makeEvent(type, parentId, eventRow, {
                    entity: { id: resolveId(entityInfo), version: entityInfo.version },
                }));
                break;
            }
            case EventType.unpublishEntities: {
                events.push(makeEvent(type, parentId, eventRow, {
                    entities: eventEntityInfo.map((it) => ({
                        id: resolveId(it),
                        version: it.version,
                    })),
                }));
                break;
            }
            case EventType.updateEntity:
            case EventType.updateAndPublishEntity: {
                const entityInfo = eventEntityInfo[0];
                if (!('fields' in entityInfo)) {
                    return notOk.Generic('Cannot find extended info about entity');
                }
                events.push(makeEvent(type, parentId, eventRow, {
                    entity: {
                        id: resolveId(entityInfo),
                        info: {
                            name: entityInfo.name,
                            version: entityInfo.version,
                            schemaVersion: entityInfo.schema_version,
                        },
                        fields: JSON.parse(entityInfo.fields),
                    },
                }));
                break;
            }
            default:
                assertExhaustive(type);
        }
    }
    return ok(events);
}
function makeEvent(type, parentId, eventRow, specific) {
    return {
        id: eventRow.uuid,
        parentId,
        type,
        createdAt: new Date(eventRow.created_at),
        createdBy: eventRow.created_by,
        ...specific,
    };
}
function resolveId(entityInfo) {
    const id = entityInfo.uuid ?? entityInfo.uuid_before_delete;
    assertIsDefined(id);
    return id;
}
//# sourceMappingURL=syncGetEvents.js.map